import { registerLocaleLoader } from '@staizen/graphene';
import { CURRENCY_DETAILS_QUERY_KEY, DEFAULT_CURRENCY_DECIMAL_POINT } from 'app/constants';
import { CashDepositResponse } from 'app/types/actions';
import { CurrencyDetailsResponse } from 'app/types/typings';
import commonStyles from 'common/commonStyles';
import Loader from 'common/components/Loader/Loader';
import DefaultError from 'common/components/StatusIndicator/DefaultError/DefaultError';
import { useGenericQuery } from 'common/hooks/useGenericQuery';
import { centraliseContent } from 'common/utils/styler';
import { API } from 'config/configureApi';
import { APP_LOCALE } from 'config/configureI18n';
import { cx } from 'emotion';
import { CASH_DEPOSIT_DETAILS_QUERY_KEY } from 'features/dashboard/components/OpenActions/constants';
import CashDepositDetailsTab from 'features/myactions/components/CashActionDetails/CashDepositDetailsTab';
import { TAG } from 'features/myactions/components/CashActionDetails/constants';
import ActionActivityTab from 'features/myactions/components/common/ActionActivityTab';
import { styles } from 'features/myactions/styles';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

registerLocaleLoader(TAG, (locale: string) => import(`./locale.${locale}.json`));

export interface CashDepositActionDetailsProps {
  id: string;
  type: string;
  isActivityTabOpen: boolean;
}

export default function CashDepositActionDetails({ id, type, isActivityTabOpen }: CashDepositActionDetailsProps): ReactElement {
  const { t } = useTranslation(TAG);
  const {
    data: cashDepositDetails,
    isFetching: isFetchingCashDepositDetails,
    error: cashDepositDetailsError,
    refetch: refetchCashDepositDetails,
  } = useGenericQuery<CashDepositResponse>(API.CASH.CASH_DEPOSIT_DETAILS(id), [CASH_DEPOSIT_DETAILS_QUERY_KEY, id]);

  const { data: currencyDetailsResponse } = useGenericQuery<CurrencyDetailsResponse>(
    API.LP.CURRENCY_DETAILS,
    [CURRENCY_DETAILS_QUERY_KEY, cashDepositDetails?.currency],
    {
      http: { params: { currency: cashDepositDetails?.currency } },
      query: { enabled: !!cashDepositDetails },
    },
  );
  const decimalPoints = (currencyDetailsResponse && currencyDetailsResponse[0].decimalPoints) || DEFAULT_CURRENCY_DECIMAL_POINT;

  const renderDefaultError = (): ReactElement => {
    return (
      <div className={commonStyles.detailsDefaultError}>
        <DefaultError
          size="small"
          errorCode={cashDepositDetailsError?.code}
          errorMessage={cashDepositDetailsError?.message}
          getDataError
          cta={{
            label: t(`${APP_LOCALE}:buttons.reload`),
            onClick: refetchCashDepositDetails,
          }}
        />
      </div>
    );
  };

  const renderCashDepositActionDetails = (): ReactElement => {
    return (
      <>
        {isActivityTabOpen && <ActionActivityTab activityDetails={cashDepositDetails?.activity} type={type} />}
        {!isActivityTabOpen && <CashDepositDetailsTab decimalPoints={decimalPoints} {...cashDepositDetails} />}
      </>
    );
  };

  return (
    <>
      {isFetchingCashDepositDetails && <Loader customClass={cx(styles.actionsItemDetailsLoader, centraliseContent(isFetchingCashDepositDetails))} />}
      {!isFetchingCashDepositDetails && !!cashDepositDetailsError && renderDefaultError()}
      {!isFetchingCashDepositDetails && !cashDepositDetailsError && renderCashDepositActionDetails()}
    </>
  );
}
